// pages/Profile.jsx
import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  FiShield, FiUser, FiMail, FiPhone, FiEdit2, FiLock, FiArrowRight
} from 'react-icons/fi';

function Profile() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phoneNumber: user?.phoneNumber || '',
  });
  const [loading, setLoading] = useState(false);
  
  // 2FA States - simplified to only track enabled status
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [is2FALoading, setIs2FALoading] = useState(false);

  useEffect(() => {
    if (token) {
      fetchTwoFactorStatus();
    }
  }, [token]);

  const fetchTwoFactorStatus = async () => {
    setIs2FALoading(true);
    try {
      const response = await api.get('/api/auth/2fa/status');
      console.log('📦 2FA Status Response:', response.data);
      
      // Handle different response formats
      if (response.data && response.data.success !== false) {
        setTwoFactorEnabled(response.data.enabled || false);
      } else {
        // If response doesn't have success, use defaults
        setTwoFactorEnabled(false);
      }
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
      // Set default value on error
      setTwoFactorEnabled(false);
    } finally {
      setIs2FALoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Only send fields that can be updated
      const updateData = {
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
      };
      
      const response = await api.put('/api/auth/update', updateData);
      
      if (response.data && response.data.success) {
        toast.success('Profile updated successfully!');
        const updatedUser = { ...user, 
          fullName: formData.fullName,
          phoneNumber: formData.phoneNumber,
        };
        localStorage.setItem('dev_user', JSON.stringify(updatedUser));
        // Reload to update context after a delay
        setTimeout(() => window.location.reload(), 1000);
      } else {
        toast.error(response.data?.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      // Handle different error types gracefully
      if (error.response?.status === 404) {
        toast.error('Profile update feature coming soon!');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      } else {
        toast.error('Failed to update profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (is2FALoading) {
    return (
      <div style={{ padding: '20px' }}>
        <h2 style={{ marginBottom: '20px' }}>👤 Profile</h2>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>👤 Profile</h2>

      {/* User Info Card */}
      <div className="card">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '20px',
          flexWrap: 'wrap',
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px',
            color: 'white',
            fontWeight: '700',
            flexShrink: 0,
          }}>
            {user?.fullName?.[0] || user?.username?.[0] || 'U'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '20px', fontWeight: '600' }}>
              {user?.fullName || user?.username}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
              <FiMail style={{ marginRight: '6px' }} />
              {user?.email}
            </div>
            {user?.phoneNumber && (
              <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                <FiPhone style={{ marginRight: '6px' }} />
                {user?.phoneNumber}
              </div>
            )}
            <div style={{ 
              marginTop: '4px',
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
            }}>
              <span style={{
                padding: '2px 12px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600',
                background: 'rgba(102, 126, 234, 0.15)',
                color: '#667eea',
              }}>
                {user?.role || 'user'}
              </span>
              <span style={{
                padding: '2px 12px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600',
                background: twoFactorEnabled ? 'rgba(46, 204, 113, 0.15)' : 'rgba(107, 122, 143, 0.15)',
                color: twoFactorEnabled ? '#2ecc71' : '#6b7a8f',
              }}>
                {twoFactorEnabled ? '🔒 2FA Enabled' : '🔓 2FA Disabled'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <button 
          className="btn btn-primary" 
          onClick={() => navigate('/profile/update')}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: '8px',
            padding: '14px',
          }}
        >
          <FiEdit2 /> Edit Profile <FiArrowRight />
        </button>
        <button 
          className="btn btn-outline" 
          onClick={() => navigate('/profile/2fa')}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: '8px',
            padding: '14px',
          }}
        >
          <FiShield /> 2FA Settings <FiArrowRight />
        </button>
      </div>

      {/* Profile Form */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">
            <FiUser style={{ marginRight: '8px' }} />
            Profile Information
          </span>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              placeholder="Your full name"
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              disabled
            />
            <small style={{ color: 'var(--text-muted)' }}>Email cannot be changed</small>
          </div>

          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              placeholder="Your phone number"
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Two-Factor Authentication Section */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">
            <FiShield style={{ marginRight: '8px', color: twoFactorEnabled ? '#2ecc71' : '#6b7a8f' }} />
            Two-Factor Authentication
          </span>
          <span style={{ 
            fontSize: '12px', 
            color: twoFactorEnabled ? '#2ecc71' : '#6b7a8f',
            background: twoFactorEnabled ? 'rgba(46, 204, 113, 0.15)' : 'rgba(107, 122, 143, 0.15)',
            padding: '2px 12px',
            borderRadius: '12px',
          }}>
            {twoFactorEnabled ? '🔒 Enabled' : '🔓 Disabled'}
          </span>
        </div>

        <p style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
          {twoFactorEnabled 
            ? 'Two-factor authentication is enabled on your account. This adds an extra layer of security.'
            : 'Add an extra layer of security to your account by enabling two-factor authentication.'}
        </p>

        <button 
          className={`btn ${twoFactorEnabled ? 'btn-outline' : 'btn-primary'}`}
          onClick={() => navigate('/profile/2fa')}
        >
          {twoFactorEnabled ? 'Manage 2FA' : 'Enable 2FA'}
        </button>
      </div>

      {/* Security Section */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">
            <FiLock style={{ marginRight: '8px' }} />
            Account Security
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button className="btn btn-outline" onClick={() => toast('Password reset feature coming soon')}>
            Change Password
          </button>
          <button className="btn btn-outline" onClick={() => toast('Session management coming soon')}>
            Manage Sessions
          </button>
        </div>
      </div>
    </div>
  );
}

export default Profile;